import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Fragment, useState } from 'react';
import { aiCodingJobQueries } from '../../hooks/backend/aiCodingJobs';
import {
  useCreateQuestionCategory,
  useDeleteQuestionCategory,
  useGenerateQuestionCategories,
  useUpdateQuestionCategory,
} from '../../hooks/backend/questionCategories';
import {
  QuestionCategory,
  QuestionCategoryType,
} from '../../services/backend/questionCategories';
import { SurveyWave } from '../../types/domainModels';
import {
  generateCategoryCode,
  getCategoryBadgeColor,
} from '../../util/aiCodingCategories';
import { downloadCSV } from '../../util/files';
import { getSurveyWaveOptions } from '../../util/surveyWaves';
import Badge from '../common/Badge';
import CircleLoader from '../common/CircleLoader';
import ErrorDisplay from '../common/ErrorDisplay';
import Button from '../common/forms/Button';
import Input from '../common/forms/Input';
import Textarea from '../common/forms/Textarea';
import Icon from '../common/Icon';
import IconBackground from '../common/icons/IconBackground';
import Listbox, { ListboxButton, ListboxOption } from '../common/Listbox';
import Tooltip from '../common/Tooltip';
import TrashIconWithConfirmation from '../common/TrashIconWithConfirmation';

const CODING_TYPE_OPTIONS = [
  {
    label: 'Theme Coding',
    value: 'THEME',
    tooltip:
      'Designed for longer, open-ended responses. This option generates higher-level themes or ideas that may span multiple concepts within a single response.',
  },
  {
    label: 'Coded List',
    value: 'LIST',
    tooltip:
      'Designed for shorter responses or lists of words or phrases. This is especially useful for questions like unaided brand awareness, where respondents list items rather than explain them.',
  },
] as const;

const MAX_CATEGORY_COUNT = 100;

interface AICodingCategoriesTabProps {
  questionId: number;
  waves: SurveyWave[];
  categories: QuestionCategory[];
  isLoading: boolean;
  categoriesError: Error | null;
  isCategoryGenerationProcessing: boolean;
  selectedWaveId: number | null | undefined;
  onWaveChange: (waveId: number | null | undefined) => void;
  onError: (error: string | null) => void;
}

const AICodingCategoriesTab = ({
  questionId,
  waves,
  categories,
  isLoading,
  categoriesError,
  isCategoryGenerationProcessing,
  selectedWaveId,
  onWaveChange,
  onError,
}: AICodingCategoriesTabProps): JSX.Element => {
  const queryClient = useQueryClient();

  const [categoryCount, setCategoryCount] = useState(10);
  const [codingType, setCodingType] = useState<QuestionCategoryType>('THEME');
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null,
  );
  const [editingName, setEditingName] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(
    null,
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const waveOptions = getSurveyWaveOptions({ waves });
  const selectedWaveOption = waveOptions.find(
    ({ value }) => value === selectedWaveId,
  );

  const createCategoryMutation = useCreateQuestionCategory({
    onSuccess: () => {
      setIsAddingNew(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      onError(null);
    },
    onError: () => {
      onError('Failed to create category');
    },
  });

  const updateCategoryMutation = useUpdateQuestionCategory({
    onSuccess: () => {
      setEditingCategoryId(null);
      setEditingName('');
      setEditingDescription('');
      onError(null);
    },
    onError: () => {
      onError('Failed to update category');
    },
  });

  const deleteCategoryMutation = useDeleteQuestionCategory({
    onSuccess: () => {
      onError(null);
    },
    onError: () => {
      onError('Failed to delete category');
    },
  });

  const generateCategoriesMutation = useGenerateQuestionCategories({
    onSuccess: async () => {
      // Invalidate AI coding jobs to start polling for job status
      await queryClient.invalidateQueries(
        aiCodingJobQueries.byQuestion({ questionId }),
      );
      onError(null);
    },
    onError: () => {
      onError('Failed to start category generation');
    },
  });

  const handleGenerate = () => {
    onError(null);
    generateCategoriesMutation.mutate({
      questionId,
      count: categoryCount,
      categoryType: codingType,
      waveId: selectedWaveId ?? undefined,
    });
  };

  const handleCreateCategory = () => {
    if (!newCategoryName.trim()) {
      onError('Category name is required');
      return;
    }

    if (createCategoryMutation.isPending) {
      return;
    }

    createCategoryMutation.mutate({
      name: newCategoryName.trim(),
      questionId,
      categoryType: codingType,
      description: newCategoryDescription.trim() || undefined,
      waveId: selectedWaveId ?? undefined,
    });
  };

  const handleUpdateCategory = (categoryId: number) => {
    if (!editingName.trim()) {
      onError('Category name is required');
      return;
    }

    if (updateCategoryMutation.isPending) {
      return;
    }

    updateCategoryMutation.mutate({
      id: categoryId,
      name: editingName.trim(),
      description: editingDescription.trim() || undefined,
    });
  };

  const handleDeleteCategory = () => {
    if (deletingCategoryId === null) {
      return;
    }

    deleteCategoryMutation.mutate(
      { id: deletingCategoryId },
      {
        onSuccess: () => {
          setIsDeleteConfirmOpen(false);
          setDeletingCategoryId(null);
        },
      },
    );
  };

  const startEditingCategory = (category: QuestionCategory) => {
    setEditingCategoryId(category.id);
    setEditingName(category.name);
    setEditingDescription(category.description || '');
  };

  const cancelEditing = () => {
    setEditingCategoryId(null);
    setEditingName('');
    setEditingDescription('');
  };

  const cancelAddingNew = () => {
    setIsAddingNew(false);
    setNewCategoryName('');
    setNewCategoryDescription('');
  };

  const downloadCategoriesCSV = () => {
    const data = categories.map((c) => ({
      Code: generateCategoryCode(c.name),
      Name: c.name,
      Description: c.description,
    }));
    downloadCSV({
      data,
      filename: `categories_${questionId}.csv`,
      errorLabel: 'categories',
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-start gap-4 mb-6">
        <Button
          disabled={isCategoryGenerationProcessing}
          hierarchy="primary"
          icon={
            isCategoryGenerationProcessing ? (
              <CircleLoader />
            ) : (
              <Icon id="lightning-01" />
            )
          }
          iconPlacement="leading"
          onClick={handleGenerate}
          size="md"
        >
          {isCategoryGenerationProcessing ? 'Generating...' : 'Generate'}
        </Button>

        {waves.length > 1 && (
          <div className="w-48">
            <Listbox
              button={
                <ListboxButton as={Fragment}>
                  <Button
                    grow
                    hierarchy="secondary-gray"
                    icon={<Icon id="chevron-down" />}
                    iconPlacement="trailing"
                    justification="justify-between"
                    size="md"
                  >
                    <span className="truncate">
                      {selectedWaveOption?.label ?? 'All Waves'}
                    </span>
                  </Button>
                </ListboxButton>
              }
              onChange={onWaveChange}
              placement="bottom-start"
              value={selectedWaveId}
            >
              {waveOptions.map((option) => {
                return (
                  <ListboxOption
                    key={option.value || 'all'}
                    value={option.value}
                  >
                    {option.label}
                  </ListboxOption>
                );
              })}
            </Listbox>
            <div className="text-sm text-gray-d-600 mb-2">Wave selection</div>
          </div>
        )}

        <div className="w-48">
          <Listbox
            button={
              <ListboxButton className="w-full rounded-lg border border-gray-d-300 shadow-sm text-gray-d-900 outline-primary-d-600 py-2 px-3 text-sm flex justify-between items-center bg-white">
                <span className="block truncate">
                  {
                    CODING_TYPE_OPTIONS.find((opt) => opt.value === codingType)
                      ?.label
                  }
                </span>
                <div className="w-4 h-4 text-gray-d-500">
                  <Icon id="chevron-down" />
                </div>
              </ListboxButton>
            }
            onChange={(value) => {
              if (value) {
                setCodingType(value);
              }
            }}
            value={codingType}
          >
            {CODING_TYPE_OPTIONS.map((option) => (
              <ListboxOption key={option.value} value={option.value}>
                {option.label}
              </ListboxOption>
            ))}
          </Listbox>
          <div className="text-sm text-gray-d-600 mb-1 flex items-center gap-1">
            Coding Type
            <Tooltip placement="bottom">
              {
                CODING_TYPE_OPTIONS.find((opt) => opt.value === codingType)
                  ?.tooltip
              }
            </Tooltip>
          </div>
        </div>

        {codingType !== 'LIST' && (
          <div className="w-48">
            <Input
              max={MAX_CATEGORY_COUNT}
              min={1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value === '') {
                  setCategoryCount(1);
                  return;
                }

                const parsedValue = parseInt(value, 10);
                if (
                  !isNaN(parsedValue) &&
                  parsedValue >= 1 &&
                  parsedValue <= MAX_CATEGORY_COUNT
                ) {
                  setCategoryCount(parsedValue);
                }
              }}
              size="md"
              step="1"
              type="number"
              value={categoryCount.toString()}
            />
            <div className="text-sm text-gray-d-600 mb-1">
              # of categories to generate
            </div>
          </div>
        )}

        <div className="ml-auto">
          <Button
            disabled={categories.length === 0}
            hierarchy="secondary-gray"
            icon={<Icon id="download" />}
            onClick={downloadCategoriesCSV}
            size="md"
          >
            Download CSV
          </Button>
        </div>
      </div>

      <div className="mb-4 text-xs text-gray-d-500 leading-relaxed">
        Glass cannot guarantee the accuracy of any results generated via AI.
        Please be sure to validate any automatically generated results.
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="border border-gray-d-200 rounded-lg bg-white flex flex-col overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-d-500">Loading categories...</div>
            </div>
          ) : categoriesError ? (
            <div className="flex items-center justify-center py-12 px-6">
              <ErrorDisplay message="Failed to load categories" />
            </div>
          ) : (
            <>
              <div className="overflow-y-auto max-h-[400px]">
                <table className="w-full">
                  <thead className="bg-gray-d-50 sticky top-0 z-10 border-b border-gray-d-200">
                    <tr>
                      <th className="py-3 px-6 text-left text-sm font-semibold text-gray-d-600 w-24">
                        Code
                      </th>
                      <th className="py-3 px-6 text-left text-sm font-semibold text-gray-d-600">
                        Name
                      </th>
                      <th className="py-3 px-6 text-left text-sm font-semibold text-gray-d-600">
                        Description
                      </th>
                      <th className="py-3 px-6 text-center text-sm font-semibold text-gray-d-600 w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td
                          className="py-12 text-center text-gray-d-500"
                          colSpan={4}
                        >
                          No categories found
                        </td>
                      </tr>
                    ) : (
                      categories.map((category, index) => (
                        <tr
                          key={category.id}
                          className={clsx(
                            'hover:bg-gray-d-200 transition-colors border-b border-gray-d-100 last:border-0',
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-d-50',
                            editingCategoryId === category.id && 'bg-blue-50',
                          )}
                        >
                          <td className="py-4 px-6 align-top">
                            <Badge
                              {...getCategoryBadgeColor(
                                category.name,
                                categories,
                              )}
                              size="sm"
                            >
                              {generateCategoryCode(category.name)}
                            </Badge>
                          </td>
                          <td className="py-4 px-6 align-top">
                            {editingCategoryId === category.id ? (
                              <Input
                                autoFocus
                                onChange={(e) => setEditingName(e.target.value)}
                                size="md"
                                value={editingName}
                              />
                            ) : (
                              <div className="font-medium text-gray-d-900 text-sm">
                                {category.name}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-6 align-top">
                            {editingCategoryId === category.id ? (
                              <Textarea
                                onChange={(e) =>
                                  setEditingDescription(e.target.value)
                                }
                                rows={2}
                                size="md"
                                value={editingDescription}
                              />
                            ) : (
                              <div className="text-sm text-gray-d-600">
                                {category.description}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-6 align-top">
                            <div className="flex items-center justify-center gap-2">
                              {editingCategoryId === category.id ? (
                                <>
                                  <IconBackground
                                    disabled={updateCategoryMutation.isPending}
                                    onClick={() =>
                                      handleUpdateCategory(category.id)
                                    }
                                    title="Save"
                                  >
                                    {updateCategoryMutation.isPending ? (
                                      <CircleLoader />
                                    ) : (
                                      <div className="w-4 h-4 text-green-600">
                                        <Icon id="check" />
                                      </div>
                                    )}
                                  </IconBackground>
                                  <IconBackground
                                    onClick={cancelEditing}
                                    title="Cancel"
                                  >
                                    <div className="w-4 h-4 text-gray-600">
                                      <Icon id="x" />
                                    </div>
                                  </IconBackground>
                                </>
                              ) : (
                                <>
                                  <IconBackground
                                    disabled={
                                      deleteCategoryMutation.isPending ||
                                      updateCategoryMutation.isPending ||
                                      createCategoryMutation.isPending
                                    }
                                    onClick={() =>
                                      startEditingCategory(category)
                                    }
                                    title="Edit"
                                  >
                                    <div className="w-4 h-4">
                                      <Icon id="edit" />
                                    </div>
                                  </IconBackground>
                                  <TrashIconWithConfirmation
                                    disabled={
                                      deleteCategoryMutation.isPending ||
                                      updateCategoryMutation.isPending ||
                                      createCategoryMutation.isPending
                                    }
                                    isConfirmOpen={
                                      isDeleteConfirmOpen &&
                                      deletingCategoryId === category.id
                                    }
                                    isDeleting={
                                      deleteCategoryMutation.isPending
                                    }
                                    name={`delete-category-${category.id}`}
                                    onConfirmDelete={handleDeleteCategory}
                                    prompt={`Are you sure you want to delete "${category.name}"?`}
                                    setIsConfirmOpen={(isOpen: boolean) => {
                                      setIsDeleteConfirmOpen(isOpen);
                                      if (isOpen) {
                                        setDeletingCategoryId(category.id);
                                      } else {
                                        setDeletingCategoryId(null);
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="border-t-2 border-gray-d-200">
                {isAddingNew ? (
                  <div className="bg-blue-50 p-4">
                    <div className="grid grid-cols-[100px_1fr_1fr_100px] gap-4 items-start">
                      <div className="flex items-center justify-center">
                        <Badge color="primary" size="sm">
                          NEW
                        </Badge>
                      </div>
                      <div>
                        <Input
                          autoFocus
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Category name"
                          size="md"
                          value={newCategoryName}
                        />
                      </div>
                      <div>
                        <Textarea
                          onChange={(e) =>
                            setNewCategoryDescription(e.target.value)
                          }
                          placeholder="Description (optional)"
                          rows={2}
                          size="md"
                          value={newCategoryDescription}
                        />
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <IconBackground
                          disabled={createCategoryMutation.isPending}
                          onClick={handleCreateCategory}
                          title="Save"
                        >
                          {createCategoryMutation.isPending ? (
                            <CircleLoader />
                          ) : (
                            <div className="w-4 h-4 text-green-600">
                              <Icon id="check" />
                            </div>
                          )}
                        </IconBackground>
                        <IconBackground
                          onClick={cancelAddingNew}
                          title="Cancel"
                        >
                          <div className="w-4 h-4 text-gray-600">
                            <Icon id="x" />
                          </div>
                        </IconBackground>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="bg-gray-d-25 hover:bg-gray-d-100 transition-colors cursor-pointer py-4"
                    onClick={() => setIsAddingNew(true)}
                  >
                    <div className="flex items-center justify-center gap-2 text-primary-d-600 font-medium text-sm">
                      <div className="w-4 h-4">
                        <Icon id="plus" />
                      </div>
                      <span>Add New Category</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AICodingCategoriesTab;
